<template>
  <div class="recommendation-parameter-interface">
    <v-select
      class="recommendation-parameter-interface__format"
      :items="formatOptions"
      v-model="format"
      :label="$ucfirst($t('format')) + '*'"
      :readonly="isValueInputDisabled"
      outlined
      dense
    ></v-select>
    <component
      :is="inputComponent"
      v-if="format === 'FORMAT_VALUE'"
      :items="valueOptions"
      v-model="singleValue"
      :label="$ucfirst($t('general.value', {catalog: 'app_mobile'})) + '*'"
      class="recommendation-parameter-interface__single-value"
      outlined
      dense
    ></component>
    <component
      :is="inputComponent"
      v-if="format === 'FORMAT_RANGE'"
      :items="valueOptions"
      v-model="min"
      :label="
        $ucfirst($t('min')) +
        (areRangeFieldsEmpty || valueOptions.length || ((min || min === 0) && !max && max !== 0) ? '*' : '')
      "
      :readonly="isValueInputDisabled"
      class="recommendation-parameter-interface__value"
      outlined
      dense
    ></component>
    <component
      :is="inputComponent"
      v-if="format === 'FORMAT_RANGE'"
      :items="valueOptions"
      v-model="max"
      :label="
        $ucfirst($t('max')) +
        (areRangeFieldsEmpty || valueOptions.length || ((max || max === 0) && !min && min !== 0) ? '*' : '')
      "
      :readonly="isValueInputDisabled"
      class="recommendation-parameter-interface__value"
      outlined
      dense
    ></component>
    <v-select
      v-if="!noDependencies"
      :items="flagOptions"
      v-model="flags"
      :label="$ucfirst($t('dependencies'))"
      class="recommendation-parameter-interface__flag"
      multiple
      outlined
      dense
    ></v-select>
    <v-btn
      v-if="isRemovable"
      color="error"
      class="mr-4 mt-1"
      fab
      text
      x-small
      right
      @click="remove"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>
  </div>
</template>

<script>
import IntegerInput from '../../../components/Parts/IntegerInput';

/**
 * Component RecommendationParameterInterface.vue
 *
 * @module RecommendationParameterInterface
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/SportsGoalBoxes/RecommendationParameterInterface.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'RecommendationParameterInterface',
  components: { IntegerInput },
  props: {
    value: {
      type: Object,
      required: true,
    },
    valueOptions: {
      type: Array,
      default: () => [],
    },
    flagOptions: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      required: false,
    },
    isRemovable: {
      type: Boolean,
      default: () => false,
    },
    isNumericInput: {
      type: Boolean,
      default: () => false,
    },
    noDependencies: {
      type: Boolean,
      default: () => false,
    },
    isValueInputDisabled: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      format: '',
      prevMin: '',
      prevMax: '',
    };
  },
  computed: {
    formatOptions() {
      return [
        {
          text: this.$ucfirst(this.$t('general.value', {catalog: 'app_mobile'})),
          value: 'FORMAT_VALUE',
        },
        {
          text: this.$ucfirst(this.$t('range')),
          value: 'FORMAT_RANGE',
        },
      ];
    },
    singleValue: {
      get() {
        return this.value.min === this.value.max ? this.value.min : '';
      },
      set(singleValue) {
        const value = singleValue && !isNaN(parseInt(singleValue))
          ? parseInt(singleValue)
          : singleValue;

        this.$emit('input', {
          min: value,
          max: value,
          flags: this.value.flags,
        });
      },
    },
    min: {
      get() {
        return this.value.min;
      },
      set(min) {
        const minValue = min && !isNaN(parseInt(min)) ? parseInt(min) : min;
        this.$emit('input', {
          min: minValue,
          max: this.value.max,
          flags: this.value.flags,
        });
      },
    },
    max: {
      get() {
        return this.value.max;
      },
      set(max) {
        const maxValue = max && !isNaN(parseInt(max)) ? parseInt(max) : max;
        this.$emit('input', {
          min: this.value.min,
          max: maxValue,
          flags: this.value.flags,
        });
      },
    },
    flags: {
      get() {
        return this.value.flags;
      },
      set(flags) {
        this.$emit('input', {
          min: this.value.min,
          max: this.value.max,
          flags,
        });
      },
    },
    inputComponent() {
      return this.isNumericInput ? 'integer-input' : 'v-select';
    },
    areRangeFieldsEmpty() {
      return !this.value.min
        && this.value.min !== 0
        && !this.value.max
        && this.value.max !== 0;
    }
  },
  watch: {
    value(newValue, oldValue) {
      const changeHappened = newValue.min !== oldValue.min || newValue.max !== oldValue.max;
      // update format only if change happened and range fields are not empty
      if (this.areRangeFieldsEmpty || !changeHappened) return;
      if (newValue.min === newValue.max) {
        this.format = 'FORMAT_VALUE';
      } else {
        this.format = 'FORMAT_RANGE';
      }
    },
    format(newValue, oldValue) {
      // clear min/max values when switching from Range to Value
      if (newValue === 'FORMAT_VALUE'
        && oldValue === 'FORMAT_RANGE'
        && !this.singleValue
        && this.singleValue !== 0
      ) {
        this.clearRangeFields()
      }
      // set previous min/max values when switching from Value to Range
      if (newValue === 'FORMAT_RANGE'
        && oldValue === 'FORMAT_VALUE'
        && this.areRangeFieldsEmpty
      ) {
        this.$emit('input', {
          min: this.prevMin,
          max: this.prevMax,
          flags: this.value.flags,
        });
      }
    }
  },
  methods: {
    remove() {
      this.$emit('remove', this.index);
    },
    clearRangeFields() {
      this.prevMin = this.value.min;
      this.prevMax = this.value.max;

      this.$emit('input', {
        min: '',
        max: '',
        flags: this.value.flags,
      });
    },
  },
  created() {
    if (this.value.min === this.value.max) {
      this.format = 'FORMAT_VALUE';
    } else {
      this.format = 'FORMAT_RANGE';
    }
  },
};
</script>

<style lang="scss">
.recommendation-parameter-interface {
  display: flex;

  .v-input {
    margin-right: 16px;
  }
}

.recommendation-parameter-interface__format {
  width: 150px;
  max-width: 150px;
  min-width: 150px;
}

.recommendation-parameter-interface__flag {
  flex-grow: 1;
}

.recommendation-parameter-interface__value {
  width: 100px;
  max-width: 100px;
  min-width: 100px;
}

.recommendation-parameter-interface__single-value {
  width: 216px;
  max-width: 216px;
  min-width: 216px;
}
</style>
